/*
  This file sets the adaptive view type of the application (desktop vs mobile)
  It will first set the view type on the server side by checking the device type
  Once it reaches the client side it will do another check for view type based on the screen size
  The screen size's view type will overwrite the device type-based view type and it will update viewtype if necessary
*/
import { computed, watch, watchEffect, onMounted } from 'vue';
import { SET_VIEW_TYPE, SET_ACTIVE_SEARCH_TRIGGER_ID, SET_SEARCH_MODAL_STATE } from '#shared/types/actions';
import useViewType from './dumb/useViewType';
import useUserPreferences from '~/composables/useUserPreferences';

function callBackOrDispatch({ callback, action, force = false }) {
  if (callback) {
    return callback(force);
  }
  return action(force);
}

export function handleViewType() {
  const pageData = usePageData();
  const navViewType = useNavViewType();
  const navHeader = useNavHeader();
  const { context } = pageData.navigation;
  const { viewType } = useViewType(context);

  watch(
    () => viewType.value,
    () => {
      if (navHeader.isSearchModalActive) {
        navHeader[SET_ACTIVE_SEARCH_TRIGGER_ID](null);
        navHeader[SET_SEARCH_MODAL_STATE](false);
      }
    },
  );

  watchEffect(() => navViewType[SET_VIEW_TYPE](viewType.value));
}

function fetchOnViewTypeChange({ action, callback }) {
  onMounted(() => {
    const navViewType = useNavViewType();
    const storedType = computed(() => navViewType.viewType);

    if (storedType.value) {
      callBackOrDispatch({ callback, action });
    }

    watch(storedType, () => storedType.value && callBackOrDispatch({ callback, action }));
  });
}

function fetchOnRegionChange({ action, callback }) {
  const pageData = usePageData();
  const isDomestic = computed(() => pageData.navigation.context.isDomestic);
  const force = true;

  watch(isDomestic, () =>
    callBackOrDispatch({
      callback,
      action,
      force,
    }),
  );
}

function handleFetch({ action, callback, checkViewTypeChange = true }) {
  if (checkViewTypeChange) {
    fetchOnViewTypeChange({ action, callback });
  } else {
    onMounted(() => {
      callBackOrDispatch({ callback, action });
    });
  }
  fetchOnRegionChange({ action, callback });
}

export function useClientInitHeader() {
  const headerStore = useNavHeader();
  handleViewType();
  handleFetch({ callback: headerStore.fetch });
  useUserPreferences();
}

export function useInitFooter() {
  const footerStore = useNavFooter();
  handleFetch({ action: footerStore.fetch, checkViewTypeChange: false });
}
